/**
 * Apple app-specific passwords (« mots de passe pour application »): four groups of four letters separated by hyphens
 * (`abcd-efgh-ijkl-mnop`, the shape Apple shows). Pure helpers shared by the Worker and the web client:
 * - redactAppPasswords / containsAppPassword: free text (messages, transcripts, mail bodies). Only hyphenated groups
 *   count there: four 4-letter words separated by spaces are ordinary French. Matches inside URLs, addresses, host
 *   names or longer hyphenated runs are not passwords.
 * - normalizeAppPassword: the connect form only, where the field holds nothing else (spaces or no separator accepted).
 * The placeholder and every fixed text must never match the pattern themselves.
 */

export const APP_PASSWORD_PLACEHOLDER = "[mot de passe pour app masqué]";

/** Hyphenated 4×4 letters, not glued to a word, an address, a path or another hyphenated group. */
const APP_PASSWORD_IN_TEXT = /(?<![\p{L}\p{N}_@./\\-])[a-z]{4}-[a-z]{4}-[a-z]{4}-[a-z]{4}(?![\p{L}\p{N}_@\\-]|\.[\p{L}\p{N}])/giu;
const DASHES = /[‐-―−﹘﹣－]/gu;
const ZERO_WIDTH = /[​-‍⁠﻿]/gu;

/** The text the pattern sees: NFKC (full-width letters), zero-width characters removed, every dash a hyphen. */
function scanCopy(text: string): string {
  return text.normalize("NFKC").replace(ZERO_WIDTH, "").replace(DASHES, "-");
}

/**
 * Masks every app-password-shaped string. Without a match the original string comes back unchanged; otherwise the
 * normalised copy with each match replaced by the placeholder (`count` matches).
 */
export function redactAppPasswords(text: string): { text: string; count: number } {
  if (!text) return { text, count: 0 };
  const scan = scanCopy(text);
  let count = 0;
  const masked = scan.replace(APP_PASSWORD_IN_TEXT, () => {
    count++;
    return APP_PASSWORD_PLACEHOLDER;
  });
  return count ? { text: masked, count } : { text, count: 0 };
}

/** The app passwords found in free text (canonical lower case), for the chat capture only. */
export function findAppPasswords(text: string): string[] {
  if (!text) return [];
  return [...scanCopy(text).matchAll(APP_PASSWORD_IN_TEXT)].map((m) => m[0].toLowerCase());
}

/**
 * An obvious placeholder rather than a password Apple generated: a group of one repeated letter (`xxxx`), four equal
 * groups, or the alphabet in order (`abcd-efgh-ijkl-mnop`). Apple's random groups almost never look like this.
 */
export function isPlaceholderAppPassword(value: string): boolean {
  const groups = value.toLowerCase().split("-");
  if (groups.length !== 4) return false;
  if (groups.some((g) => /^(.)\1{3}$/.test(g)) || new Set(groups).size === 1) return true;
  const run = (g: string) => [...g].every((c, i) => i === 0 || c.charCodeAt(0) === g.charCodeAt(i - 1) + 1);
  return groups.every(run);
}

export function containsAppPassword(text: string): boolean {
  return redactAppPasswords(text).count > 0;
}

/**
 * The connect form's field → canonical `abcd-efgh-ijkl-mnop` (lower case), or null when it is not shaped like an app
 * password (the main Apple password is refused before anything leaves). Accepts hyphens, spaces or no separator.
 */
export function normalizeAppPassword(input: string): string | null {
  const s = scanCopy(input).trim().toLowerCase();
  const m = /^([a-z]{4})[-\s]?([a-z]{4})[-\s]?([a-z]{4})[-\s]?([a-z]{4})$/u.exec(s);
  return m ? `${m[1]}-${m[2]}-${m[3]}-${m[4]}` : null;
}
